import { useEffect, useRef, useState } from "react";
import { useDebounceValue } from "usehooks-ts";

import { useMarket } from "#/store/app/portfolio/market";
import { useShowTradePanel } from "#/store/app/tradePanel/showTradePanel";
import { useMaturity } from "#/store/options/page/maturity";
import { usePositions } from "#/store/wallet/account/positions";
import { type Position } from "#/types/positions";
import { nextMaturities } from "#/utils/maturities";

interface MaturityWithIndicators {
  maturity: string;
  indicators: {
    long: boolean;
    short: boolean;
  };
}

const getMaturitiesWithIndicators = (
  maturities: string[],
  positions: Position[],
): MaturityWithIndicators[] => {
  return maturities.map((maturity) => {
    const positionsForMaturity = positions.filter(
      (position) => position.maturity === maturity,
    );
    return {
      maturity,
      indicators: {
        long: positionsForMaturity.some(({ size }) => size > 0),
        short: positionsForMaturity.some(({ size }) => size < 0),
      },
    };
  });
};

const calculateVisibleItems = (
  wrapperRef: React.RefObject<HTMLDivElement | null>,
  maturities: MaturityWithIndicators[],
): MaturityWithIndicators[] => {
  if (!wrapperRef.current) return maturities;

  // 38 is the width of the menu icon + the gap between the items and menu icon
  const wrapperWidth = wrapperRef.current.offsetWidth - 38;
  const itemWidth = 140; // width of each item

  return maturities.reduce<MaturityWithIndicators[]>((visibleItems, item) => {
    const currentTotalWidth = visibleItems.length * itemWidth;
    const nextTotalWidth = currentTotalWidth + itemWidth;

    return nextTotalWidth <= wrapperWidth
      ? [...visibleItems, item]
      : visibleItems;
  }, []);
};

export const useMaturitySelectorController = () => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const { maturity, setMaturity } = useMaturity();
  const { showTradePanel } = useShowTradePanel();
  const { market } = useMarket();
  const { positions } = usePositions(market);
  const positionsKey = positions.reduce(
    (acc, position) => acc + position.maturity,
    "",
  );

  const maturities = getMaturitiesWithIndicators(nextMaturities(), positions);

  const [visibleItems, setVisibleItems] =
    useState<MaturityWithIndicators[]>(maturities);
  const [debouncedShowTradePanel] = useDebounceValue(showTradePanel, 500);

  useEffect(() => {
    const handleResize = () => {
      const newVisibleItems = calculateVisibleItems(wrapperRef, maturities);
      setVisibleItems(newVisibleItems);
    };

    window.addEventListener("resize", handleResize);

    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- adding maturities array creates infinite loop
  }, [positionsKey, debouncedShowTradePanel]);

  return {
    maturity,
    maturities,
    visibleItems,
    wrapperRef,
    setMaturity,
  };
};
